import { Box, alpha } from '@mui/material';
import type { ReactNode } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';

import { LeadItem } from '../LeadItem/LeadItem';
import type {
    DesignSystemEmptyStateType,
    DesignSystemEmptyStateSize,
} from '../../theme/design-system/types';
import {
    ringConfigMap,
    getRingColor,
    getFeatureIconConfig,
    sizeToLeadItemSize,
} from './EmptyState.styleUtils';

type RingProps = {
    size: number;
    color: string;
    opacity: number;
    children: ReactNode;
};

const Ring = ({ size, color, opacity, children }: RingProps) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: size,
            height: size,
            borderRadius: '50%',
            border: `1px solid ${alpha(color, opacity)}`,
        }}
    >
        {children}
    </Box>
);

type EmptyStateIconProps = {
    icon: RemixiconComponentType;
    size: DesignSystemEmptyStateSize;
    type: DesignSystemEmptyStateType;
};

export const EmptyStateIcon = ({ icon, size, type }: EmptyStateIconProps) => {
    const ringConfig = ringConfigMap[size];
    const { featureIconStyle, colorScheme } = getFeatureIconConfig(type);
    const leadItemSize = sizeToLeadItemSize[size];

    const featureIcon = (
        <LeadItem
            type="feature-icon"
            colorScheme={colorScheme}
            style={featureIconStyle}
            icon={icon}
            size={leadItemSize}
            radius="full"
        />
    );

    if (!ringConfig) {
        return featureIcon;
    }

    const ringColor = getRingColor(type);

    return ringConfig.sizes.reduceRight<ReactNode>(
        (innerContent, ringSize, index) => (
            <Ring
                size={ringSize}
                color={ringColor}
                opacity={ringConfig.opacities[index]}
            >
                {innerContent}
            </Ring>
        ),
        featureIcon,
    );
};
